import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type MouseEvent } from "react";
import {
  YOUTUBE_NOCOOKIE_ORIGIN,
  isYoutubePlayerReadyEvent,
  postYoutubeCommand,
  postYoutubeListening,
  readYoutubeMessage,
} from "@/shared/youtube/youtubePlayer";
import { useLocale } from "@/shared/i18n/LocaleContext";
import styles from "./Hero.module.css";

const CHROME_HIDE_MS = 2200;
/** Quanto o disco segura antes de começar a sair — o mesmo tempo que o selo
 *  "powered by AutoU" usava, que era o que estava certo. O botão cinza do
 *  YouTube some entre 3,1s e 3,6s depois que o vídeo volta a rodar (medido no
 *  player), então esperamos passar disso com folga; senão o disco sai e ainda
 *  dá tempo de ver o botão do YouTube apagando atrás. */
const ESCUDO_MS = 4600;

function isTouchUi() {
  return window.matchMedia("(hover: none)").matches;
}

type PlayerCommand =
  | "playVideo"
  | "pauseVideo"
  | "mute"
  | "unMute"
  | "setVolume"
  | "seekTo"
  | "unloadModule"
  | "getCurrentTime"
  | "getDuration";

/**
 * Regras que mandam neste componente (documentação do YouTube + política de
 * autoplay dos navegadores):
 *
 * 1. Autoplay COM som não existe em visita fria. Pior: mandar `unMute` sem
 *    interação faz o navegador PAUSAR o vídeo. Então começa mudo e o som só é
 *    pedido depois de um gesto de verdade.
 * 2. Gesto de verdade é `click`, `pointerup`/`touchend` (toque) ou `keydown` —
 *    rolagem NÃO conta em navegador nenhum. Por isso escutamos esses quatro e
 *    nunca damos o som por certo: quem confirma é o próprio player.
 * 3. O título/avatar do YouTube ficam no topo do iframe; a barra nativa, no
 *    rodapé. O iframe 3× mais alto empurra os dois pra fora da janela. Sem capa:
 *    um scrim da cor da moldura esconde o thumbnail do YouTube até o primeiro
 *    frame. Na pausa o quadro do vídeo permanece. O botão dourado central tapa o
 *    play/pause nativo (bezel ~110px) e some enquanto o vídeo toca.
 * 4. Controle nativo não é estilizável (iframe cross-origin). Os botões daqui
 *    são nossos e falam com o player pela API.
 */
function embedUrl(videoId: string, origin: string) {
  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    playsinline: "1",
    controls: "0",
    disablekb: "1",
    rel: "0",
    fs: "0",
    iv_load_policy: "3",
    cc_load_policy: "0",
    enablejsapi: "1",
    // Sem `loop`/`playlist` de propósito: o loop nativo do YouTube exige uma
    // playlist, e playlist faz o player ganhar botões de anterior/próximo e a
    // chance de sair pra outro vídeo. O loop é feito aqui, no fim do vídeo.
  });
  if (origin) params.set("origin", origin);
  return `${YOUTUBE_NOCOOKIE_ORIGIN}/embed/${videoId}?${params.toString()}`;
}

type Props = {
  videoId: string;
  /** Corte vertical da mesma gravação, pro celular. */
  videoIdVertical?: string;
  title: string;
};

/** Mesmo ponto de corte do CSS (`@media (max-width: 900px)`), senão a moldura
 *  vira retrato com o vídeo widescreen dentro (ou o contrário). */
const RETRATO = "(max-width: 900px)";

export function HeroVideo({ videoId, videoIdVertical, title }: Props) {
  const isEn = useLocale().locale === "en";
  const wrapRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const restartedRef = useRef(false);
  const apiReadyRef = useRef(false);
  const soundByHandRef = useRef(false);
  const selfPausedRef = useRef(false);
  const nudgesRef = useRef(0);
  const aVistaRef = useRef(true);
  const pausaPorScrollRef = useRef(false);
  const chromeOnRef = useRef(false);
  const hideTimerRef = useRef(0);
  const volumeRef = useRef(100);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  // Marca do último comando que acendeu o botão cinza do YouTube. Começa aceso:
  // o autoplay já entra com ele na tela.
  const [escudoDesde, setEscudoDesde] = useState(1);
  const [travado, setTravado] = useState(false);
  const tempoRef = useRef(0);
  const [face, setFace] = useState<"play" | "pause">("play");
  const firstPlayDoneRef = useRef(false);
  const [soundOn, setSoundOn] = useState(false);
  const [volume, setVolume] = useState(100);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [chromeOn, setChromeOn] = useState(false);
  // Começa no widescreen porque é ele que está no HTML pré-renderizado (a build
  // não sabe o tamanho da tela); no celular o efeito troca logo na hidratação.
  const [retrato, setRetrato] = useState(false);
  // Vazio no SSR/hidratação pra o src do iframe bater; o origin entra depois.
  const [pageOrigin, setPageOrigin] = useState("");
  const src = useMemo(
    () => embedUrl(retrato && videoIdVertical ? videoIdVertical : videoId, pageOrigin),
    [retrato, videoId, videoIdVertical, pageOrigin],
  );

  const acenderEscudo = useCallback(() => setEscudoDesde(Date.now()), []);

  const sendCommand = useCallback((func: PlayerCommand, args: unknown[] = []) => {
    if (!apiReadyRef.current) return;
    postYoutubeCommand(frameRef.current, func, args);
  }, []);

  // Pede o som. Se o navegador barrar, o player continua mudo e o botão fica —
  // por isso não marcamos `soundOn` aqui: quem responde é o `muted` do player.
  const askForSound = useCallback(() => {
    selfPausedRef.current = false;
    acenderEscudo();
    sendCommand("unMute");
    sendCommand("setVolume", [volumeRef.current || 100]);
    sendCommand("playVideo");
  }, [acenderEscudo, sendCommand]);

  const concealChrome = useCallback(() => {
    window.clearTimeout(hideTimerRef.current);
    chromeOnRef.current = false;
    setChromeOn(false);
    setVolumeOpen(false);
  }, []);

  const revealChrome = useCallback(() => {
    chromeOnRef.current = true;
    setChromeOn(true);
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      chromeOnRef.current = false;
      setChromeOn(false);
      if (isTouchUi()) setVolumeOpen(false);
    }, CHROME_HIDE_MS);
  }, []);

  const togglePlay = useCallback(() => {
    acenderEscudo();
    // Travado = o player diz que toca mas nada anda; o clique ali é pra
    // destravar, não pra pausar de novo.
    if (playing && !travado) {
      selfPausedRef.current = true;
      sendCommand("pauseVideo");
      setPlaying(false);
      return;
    }
    selfPausedRef.current = false;
    sendCommand("playVideo");
    if (!soundByHandRef.current) askForSound();
  }, [playing, travado, acenderEscudo, askForSound, sendCommand]);

  const onSoundClick = useCallback(() => {
    soundByHandRef.current = true;
    revealChrome();
    // Mouse: o slider já sobe no hover. Clique liga/desliga o som.
    // Toque: o 1º toque abre a barra; o 2º muta (não existe hover).
    if (isTouchUi() && !volumeOpen) {
      setVolumeOpen(true);
      return;
    }
    if (soundOn) {
      acenderEscudo();
      sendCommand("mute");
      setSoundOn(false);
      return;
    }
    askForSound();
  }, [volumeOpen, soundOn, acenderEscudo, askForSound, sendCommand, revealChrome]);

  const onVolumeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = Number(event.currentTarget.value);
      volumeRef.current = next;
      setVolume(next);
      soundByHandRef.current = true;
      revealChrome();
      sendCommand("setVolume", [next]);
      if (next === 0) {
        sendCommand("mute");
        setSoundOn(false);
        return;
      }
      if (!soundOn) {
        sendCommand("unMute");
        setSoundOn(true);
      }
    },
    [soundOn, sendCommand, revealChrome],
  );

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    void wrapRef.current?.requestFullscreen?.();
  }, []);

  const onWrapClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target instanceof Node && controlsRef.current?.contains(event.target)) return;
      if (isTouchUi()) {
        if (chromeOnRef.current) concealChrome();
        else revealChrome();
        return;
      }
      togglePlay();
    },
    [concealChrome, revealChrome, togglePlay],
  );

  useEffect(() => {
    setPageOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    if (!videoIdVertical) return undefined;
    const mq = window.matchMedia(RETRATO);
    const sync = () => setRetrato(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [videoIdVertical]);

  useEffect(() => () => window.clearTimeout(hideTimerRef.current), []);

  // Toda troca de estado (autoplay mudo da carga, play e pause nossos, pausa por
  // scroll, volta do fim do vídeo) reacende o escudo: é exatamente aí que o
  // YouTube desenha o play/pause dele embaixo do nosso.
  // No primeiro o ícone continua o de play: some com cara de quem já estava lá,
  // em vez de piscar um "pause" que ninguém pediu.
  useEffect(() => {
    acenderEscudo();

    if (playing) {
      if (firstPlayDoneRef.current) setFace("pause");
      firstPlayDoneRef.current = true;
      return undefined;
    }

    if (!firstPlayDoneRef.current) {
      setFace("play");
      return undefined;
    }

    setFace("pause");
    const toPlay = window.setTimeout(() => setFace("play"), 450);
    return () => window.clearTimeout(toPlay);
  }, [playing, acenderEscudo]);

  // A contagem só corre com o vídeo rodando: parado, o botão do YouTube fica
  // firme na tela, então o disco também fica — sem prazo pra sair.
  useEffect(() => {
    if (!escudoDesde || !playing) return undefined;
    const apagar = window.setTimeout(() => setEscudoDesde(0), ESCUDO_MS);
    return () => window.clearTimeout(apagar);
  }, [escudoDesde, playing]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === wrapRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    apiReadyRef.current = false;
    nudgesRef.current = 0;

    const pingListening = () => {
      postYoutubeListening(frame, "hero-video");
    };

    const onPlayerReady = () => {
      if (apiReadyRef.current) return;
      apiReadyRef.current = true;
      sendCommand("playVideo");
    };

    const onMessage = (event: MessageEvent) => {
      const payload = readYoutubeMessage(event, frame);
      if (!payload) return;

      if (isYoutubePlayerReadyEvent(payload.event)) onPlayerReady();

      const info = payload.info;
      const muted = info?.muted;
      if (muted !== undefined) {
        setSoundOn(!muted);
        // Som ligado pela primeira vez: volta pro começo, ninguém perde a
        // abertura por causa do trecho que rodou mudo.
        if (!muted && !restartedRef.current) {
          restartedRef.current = true;
          acenderEscudo();
          sendCommand("seekTo", [0, true]);
          sendCommand("playVideo");
        }
      }

      if (typeof info?.duration === "number" && info.duration > 0) {
        setDuration(info.duration);
      }
      if (typeof info?.currentTime === "number") {
        tempoRef.current = info.currentTime;
        setCurrentTime(info.currentTime);
      }
      if (typeof info?.volume === "number" && info.volume > 0) {
        volumeRef.current = info.volume;
        setVolume(info.volume);
      }

      const state = info?.playerState;
      if (state === 1) {
        setPlaying(true);
        setStarted(true);
        sendCommand("unloadModule", ["captions"]); // o módulo carrega com o vídeo
      }
      if (state === 0 || state === 2) setPlaying(false);

      // Acabou: volta pro início na hora. O iframe 3× esconde a tela de
      // "vídeo relacionado" do YouTube no terço de baixo.
      if (state === 0) {
        selfPausedRef.current = false;
        acenderEscudo();
        sendCommand("seekTo", [0, true]);
        sendCommand("playVideo");
      }

      // Pausou sem ninguém pedir (política do navegador, extensão): insiste,
      // senão o overlay de título/compartilhar do YouTube aparece.
      if (state === 2 && !selfPausedRef.current && nudgesRef.current < 3) {
        nudgesRef.current += 1;
        acenderEscudo();
        sendCommand("playVideo");
      }
    };

    window.addEventListener("message", onMessage);
    frame.addEventListener("load", pingListening);

    // O iframe já vem no HTML pré-renderizado, então quase sempre ele carrega
    // ANTES do React hidratar — o evento `load` já passou. Só o `listening` é
    // seguro de repetir; comando cedo demais derruba o widget do YouTube.
    pingListening();
    const retry = setInterval(() => {
      if (apiReadyRef.current) clearInterval(retry);
      else pingListening();
    }, 250);
    const giveUp = setTimeout(() => clearInterval(retry), 15000);

    return () => {
      apiReadyRef.current = false;
      clearInterval(retry);
      clearTimeout(giveUp);
      frame.removeEventListener("load", pingListening);
      window.removeEventListener("message", onMessage);
    };
  }, [acenderEscudo, sendCommand, src]);

  // Além de alimentar a barrinha, isso é o vigia do disco dourado: se o player
  // diz "tocando" mas o relógio não anda (o navegador barrou o play com som, o
  // vídeo travou no buffer, o YouTube pausou sozinho), é aí que o botão nativo
  // do YouTube aparece. Sem esperar evento nenhum: parou de andar, o disco volta.
  useEffect(() => {
    if (!playing) return undefined;
    let ultimo = -1;
    let paradas = 0;
    const tick = window.setInterval(() => {
      sendCommand("getCurrentTime");
      sendCommand("getDuration");
      const agora = tempoRef.current;
      if (agora === ultimo) {
        paradas += 1;
        if (paradas >= 2) setTravado(true); // ~0,8s parado
      } else {
        paradas = 0;
        setTravado(false);
      }
      ultimo = agora;
    }, 400);
    return () => {
      window.clearInterval(tick);
      setTravado(false);
    };
  }, [playing, sendCommand]);

  // Vídeo fora de vista não fica falando sozinho: senão o áudio dele atropela os
  // depoimentos lá embaixo. Volta a tocar quando o hero volta — a não ser que
  // quem tenha pausado seja o usuário, aí fica pausado mesmo.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const aVista = entry.isIntersecting && entry.intersectionRatio >= 0.4;
        aVistaRef.current = aVista;

        if (!aVista) {
          if (!apiReadyRef.current) return;
          if (selfPausedRef.current) return;
          pausaPorScrollRef.current = true;
          selfPausedRef.current = true;
          sendCommand("pauseVideo");
          setPlaying(false);
          return;
        }

        if (!pausaPorScrollRef.current) return;
        pausaPorScrollRef.current = false;
        selfPausedRef.current = false;
        sendCommand("playVideo");
      },
      { threshold: [0, 0.4, 1] },
    );
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [sendCommand]);

  // Primeiro gesto em qualquer lugar da página liga o som. Rolagem não vale —
  // navegador nenhum aceita `wheel`/`scroll` como interação.
  useEffect(() => {
    if (soundOn || soundByHandRef.current) return undefined;

    // Clique na barra de controle não passa por aqui: quem aperta "pausar" não
    // quer que a página mande tocar de volta no mesmo clique.
    const onGesture = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && controlsRef.current?.contains(target)) return;
      // Clique lá embaixo, com o hero fora de vista, não pode ressuscitar o
      // vídeo do topo por cima dos depoimentos.
      if (!aVistaRef.current) return;
      // Nem o clique que acabou de pausar: era ele que fazia o vídeo voltar
      // sozinho ~0,3s depois da pausa (o disco então sumia achando que estava
      // tocando de novo).
      if (selfPausedRef.current) return;
      askForSound();
    };

    const events = ["click", "pointerup", "touchend", "keydown"] as const;
    for (const name of events) {
      window.addEventListener(name, onGesture, { passive: true });
    }
    return () => {
      for (const name of events) window.removeEventListener(name, onGesture);
    };
  }, [soundOn, askForSound]);

  return (
    <div
      className={styles.videoWrap}
      ref={wrapRef}
      data-fullscreen={fullscreen || undefined}
      data-paused={playing ? undefined : true}
      data-chrome={chromeOn || undefined}
      onClick={onWrapClick}
      onMouseLeave={() => {
        if (!isTouchUi()) setVolumeOpen(false);
      }}
    >
      <div className={styles.player}>
        <iframe
          ref={frameRef}
          src={src}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      </div>

      <div className={styles.scrim} data-off={started || undefined} aria-hidden="true" />

      <button
        type="button"
        className={styles.play}
        data-off={(playing && !escudoDesde && !travado) || undefined}
        data-playing={(face === "pause" && !travado) || undefined}
        onClick={(event) => {
          event.stopPropagation();
          togglePlay();
        }}
        aria-label={playing && !travado ? (isEn ? "Pause" : "Pausar") : isEn ? "Play the video" : "Reproduzir o vídeo"}
        tabIndex={playing && !escudoDesde && !travado ? -1 : 0}
      >
        {face === "pause" && !travado ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 5h4v14H7zm6 0h4v14h-4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 35 35" aria-hidden="true">
            <path d="M33 15.8a2 2 0 0 1 0 3.4L4 34.7A2 2 0 0 1 1 33V2a2 2 0 0 1 3-1.7z" />
          </svg>
        )}
      </button>

      <div
        className={styles.controls}
        ref={controlsRef}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={revealChrome}
      >
        <div className={styles.progressLabel} aria-hidden="true">
          <div
            className={styles.progress}
            style={{ "--progress": `${duration > 0 ? (currentTime / duration) * 100 : 0}%` } as CSSProperties}
          />
        </div>

        <div className={styles.dock}>
          <button
            type="button"
            className={styles.ctrl}
            onClick={togglePlay}
            aria-label={playing ? (isEn ? "Pause" : "Pausar") : isEn ? "Play" : "Reproduzir"}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 5h4v14H7zm6 0h4v14h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 11.1a1 1 0 0 1 0 1.8l-13 7.3A1 1 0 0 1 5.5 19V5a1 1 0 0 1 1.5-.9z" />
              </svg>
            )}
          </button>

          <div className={styles.sound} data-open={volumeOpen || undefined}>
            <label className={styles.volumeRail}>
              <span className={styles.srOnly}>Volume</span>
              <input
                type="range"
                className={styles.volume}
                min={0}
                max={100}
                step={1}
                value={soundOn ? volume : 0}
                onChange={onVolumeInput}
                onPointerDown={(event) => event.stopPropagation()}
                aria-orientation="vertical"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={soundOn ? volume : 0}
                style={{ "--volume": `${soundOn ? volume : 0}%` } as CSSProperties}
              />
            </label>
            <button
              type="button"
              className={styles.ctrl}
              onClick={onSoundClick}
              aria-label={soundOn ? (isEn ? "Adjust volume" : "Ajustar volume") : isEn ? "Turn sound on" : "Ativar o som"}
              aria-expanded={volumeOpen}
            >
              {soundOn && volume > 0 ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 9h3.5L12 4.8v14.4L7.5 15H4zm11.5-.4a5 5 0 0 1 0 6.8l-1.4-1.4a3 3 0 0 0 0-4zm2.6-2.7a8.6 8.6 0 0 1 0 12.2l-1.4-1.4a6.6 6.6 0 0 0 0-9.4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 9h3.5L12 4.8v14.4L7.5 15H4zm11 .6L16.4 8l2 2 2-2 1.4 1.4-2 2 2 2-1.4 1.4-2-2-2 2L15 13.4l2-2z" />
                </svg>
              )}
            </button>
          </div>

          <button
            type="button"
            className={styles.ctrl}
            onClick={toggleFullscreen}
            aria-label={fullscreen ? (isEn ? "Exit full screen" : "Sair da tela cheia") : isEn ? "Full screen" : "Tela cheia"}
          >
            {fullscreen ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 4h2v5a2 2 0 0 1-2 2H4V9h5zm6 0h-2v5a2 2 0 0 0 2 2h5V9h-5zM9 20h2v-5a2 2 0 0 0-2-2H4v2h5zm6 0h-2v-5a2 2 0 0 1 2-2h5v2h-5z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 9V4h5v2H6v3zm11-5h5v5h-2V6h-3zM4 15h2v3h3v2H4zm14 0h2v5h-5v-2h3z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
